import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getEvent, listEventRegistrations, listEventSubscriptions } from "../../firebase/db.js";
import { normalizeRegistrationFields } from "../registration/formConfig.js";
import { urlFor } from "../../firebase/storage.js";
import { useAuth } from "../auth/AuthContext.jsx";
import SignIn from "../auth/SignIn.jsx";

export default function EventResponses() {
  const { slug } = useParams();
  const { user, loading, configured } = useAuth();
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!configured || !user || !slug) return;
    getEvent(slug).then(setEvent).catch(() => setEvent(null));
    listEventRegistrations(slug)
      .then(setRegistrations)
      .catch((e) => {
        if (e.message?.includes("403")) setForbidden(true);
        else setRegistrations([]);
      });
    listEventSubscriptions(slug)
      .then(setSubscriptions)
      .catch((e) => {
        if (!e.message?.includes("403")) setSubscriptions([]);
      });
  }, [configured, user, slug]);

  const registrationFields = useMemo(
    () => normalizeRegistrationFields(event?.details?.eventRegistrationFields || []),
    [event?.details?.eventRegistrationFields]
  );

  if (loading) return <div className="container" style={{ paddingTop: 80 }}>Loading…</div>;
  if (!user) return <SignIn />;
  if (forbidden) return <div className="container" style={{ paddingTop: 80 }}>You don't have access to this event's responses.</div>;

  return (
    <div className="container stack" style={{ paddingTop: 48, paddingBottom: 80, maxWidth: 920 }}>
      <div className="row">
        <div>
          <h1 style={{ margin: 0 }}>Event responses</h1>
          <p className="muted" style={{ margin: "8px 0 0" }}>{event?.details?.title || slug}</p>
        </div>
        <div className="spacer" />
        <Link className="btn-ghost btn" to={`/app/edit/${slug}`}>Edit event</Link>
      </div>

      <div className="card stack" style={{ padding: 16 }}>
        <div className="row">
          <strong>Event registrations</strong>
          <div className="spacer" />
          <button
            type="button"
            className="btn-ghost btn mini"
            onClick={() => listEventRegistrations(slug).then(setRegistrations).catch(() => setRegistrations([]))}
          >
            ↻
          </button>
        </div>
        {registrations === null && <p className="muted" style={{ margin: 0 }}>Loading registrations…</p>}
        {registrations?.length === 0 && <p className="muted" style={{ margin: 0 }}>No event registrations yet.</p>}
        {!!registrations?.length && (
          <div className="stack" style={{ gap: 8 }}>
            {registrations.map((registration) => (
              <div key={registration.id} className="subscriber-row">
                <strong>{new Date(registration.createdAt).toLocaleString()}</strong>
                {registrationFields.map((field) => {
                  const value = registration.values?.[field.id];
                  if (!value) return null;
                  return field.kind === "image" ? (
                    <a key={field.id} href={urlFor(value)} target="_blank" rel="noreferrer">{field.label}</a>
                  ) : (
                    <span key={field.id}><strong>{field.label}:</strong> {value}</span>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card stack" style={{ padding: 16 }}>
        <div className="row">
          <strong>Update subscribers</strong>
          <div className="spacer" />
          <button
            type="button"
            className="btn-ghost btn mini"
            onClick={() => listEventSubscriptions(slug).then(setSubscriptions).catch(() => setSubscriptions([]))}
          >
            ↻
          </button>
        </div>
        {subscriptions === null && <p className="muted" style={{ margin: 0 }}>Loading subscribers…</p>}
        {subscriptions?.length === 0 && <p className="muted" style={{ margin: 0 }}>No subscribers yet.</p>}
        {!!subscriptions?.length && (
          <div className="stack" style={{ gap: 8 }}>
            {subscriptions.map((sub) => (
              <div key={sub.id} className="subscriber-row">
                <strong>{new Date(sub.createdAt).toLocaleString()}</strong>
                {sub.mobile && <span>{sub.mobile}</span>}
                {sub.email && <span>{sub.email}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
